import React from 'react';
import { observer } from 'src/utils/mobx-react';
import {
    HeaderItem,
    HeaderTitle,
    HeaderWrapper,
    MainTableHeadCellWrapper,
    MainTableCellWrapper,
    NetDepositContainer,
    ShowMoreWrapper,
} from './AutomaticWithdrawalHistory.style';
import { format } from 'date-fns';
import { BankTransferPaymentMethodSchema, FullPaymentMethodSchema } from 'src_server/types/wallet/netDeposits';
import {
    CombinedType,
    NetDepositState,
} from 'src/components/FraudAndCompliance/AutomaticWithdrawals/CommonParts/WithdrawalHistory//WithdrawalHistory';
import { DateTime } from 'src_common/utils/time/time';
import { Amount } from 'src_common/common/amount/Amount';
import { CurrencyType } from 'src_common/common/amount/website-money/currency';
import { Button } from 'src/domains/common/components/button/button';

const generateCardDetails = (element: CombinedType): string => {
    const bankCheck = BankTransferPaymentMethodSchema.safeParse(element.paymentMethod);
    const fullCheck = FullPaymentMethodSchema.safeParse(element.paymentMethod);
    if (fullCheck.success) {
        const isYaspa = fullCheck.data.type === 'open-banking';
        const cardNumber = `**** **** **** ${fullCheck.data.number.slice(-4)}`;
        const cardExpirationDate = fullCheck.data.expires ?? null;
        const label = isYaspa
            ? fullCheck.data.name
            : `${cardNumber} ${cardExpirationDate === null ? '' : format(new Date(cardExpirationDate), 'MM/yy')}`;
        return label;
    } else if (bankCheck.success) {
        return 'Bank Transfer';
    }

    return '--';
};

const getStatus = (amount: number): 'positive' | 'negative' | 'zero' => {
    if (amount > 0) {
        return 'positive';
    } else if (amount < 0) {
        return 'negative';
    }

    return 'zero';
};

const SingleNetDepositColumn = ({
    el,
    currency,
    isCustomerTab,
}: {
    el: CombinedType;
    currency: CurrencyType;
    isCustomerTab: boolean;
}): React.ReactNode => {
    const label = generateCardDetails(el);
    const totalAmount = parseFloat(el.aggregations.totalAmount);
    const valueType = getStatus(totalAmount);
    const valueToDisplay = new Amount(el.aggregations.totalAmount).format(currency, { withoutZeros: true });
    return (
        <div
            key={el.paymentMethod.id}
            style={{ display: 'flex', flexDirection: 'column' }}
        >
            <MainTableHeadCellWrapper
                isCustomerTab={isCustomerTab}
                data-test='main-table-head-cell-wrapper'
            >
                {label}
            </MainTableHeadCellWrapper>

            {isCustomerTab && (
                <MainTableHeadCellWrapper
                    isCustomerTab={isCustomerTab}
                    style={{ fontSize: '10px' }}
                    data-test='main-table-first-used-wrapper'
                >
                    {el.paymentMethod.id === 'bank-transfer'
                        ? '--'
                        : `First used: ${DateTime.from(el.aggregations.firstTransaction)?.format('DD-MM-YYYY HH:mm')}`}
                </MainTableHeadCellWrapper>
            )}
            <MainTableCellWrapper
                isCustomerTab={isCustomerTab}
                valueType={valueType}
                data-test='main-table-cell-wrapper'
            >
                {valueToDisplay}
            </MainTableCellWrapper>
        </div>
    );
};

const EmptyNetDepositColumn = ({ isCustomerTab }: { isCustomerTab: boolean }): React.ReactNode => {
    const label = '--';

    return (
        <div
            key='empty'
            style={{ display: 'flex', flexDirection: 'column' }}
        >
            <MainTableHeadCellWrapper
                isCustomerTab={isCustomerTab}
                data-test='main-table-head-cell-wrapper'
            >
                {label}
            </MainTableHeadCellWrapper>

            {isCustomerTab && (
                <MainTableHeadCellWrapper
                    isCustomerTab={isCustomerTab}
                    style={{ fontSize: '10px' }}
                    data-test='main-table-first-used-wrapper'
                >
                    {label}
                </MainTableHeadCellWrapper>
            )}

            <MainTableCellWrapper
                isCustomerTab={isCustomerTab}
                data-test='main-table-cell-wrapper'
            >
                {label}
            </MainTableCellWrapper>
        </div>
    );
};

type NetDepositPropsType = {
    currency: CurrencyType;
    netDepositState: NetDepositState;
    isCustomerTab?: boolean;
};

export const NetDeposit = observer(
    'NetDeposit',
    ({ currency, netDepositState, isCustomerTab = false }: NetDepositPropsType) => {
        const {
            showExpiredCards,
            activeOrBankTransfer,
            expiredCards,
            allPaymentMethods,
            onShowExpiredCards,
            netDepositSum,
            getNetDepositFormattedSum,
            isExpiredCardsExist,
        } = netDepositState;

        const methodsToRender = isCustomerTab
            ? showExpiredCards
                ? [...activeOrBankTransfer, ...expiredCards]
                : activeOrBankTransfer
            : allPaymentMethods;
        const sum = getNetDepositFormattedSum(allPaymentMethods);
        const netDepositSumVal = netDepositSum(allPaymentMethods);
        const valueType = getStatus(netDepositSumVal);
        return (
            <div data-test='net-deposit'>
                <NetDepositContainer>
                    <HeaderWrapper>
                        <HeaderItem>
                            <HeaderTitle data-test='header-title'>Net Deposits</HeaderTitle>
                        </HeaderItem>
                    </HeaderWrapper>

                    <div style={{ display: 'flex', overflowX: 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <MainTableHeadCellWrapper
                                data-test='main-table-head-cell-wrapper'
                                isCustomerTab={isCustomerTab}
                            >
                                Total
                            </MainTableHeadCellWrapper>
                            {isCustomerTab && (
                                <MainTableHeadCellWrapper
                                    data-test='main-table-first-used-wrapper'
                                    isCustomerTab={isCustomerTab}
                                >
                                    --
                                </MainTableHeadCellWrapper>
                            )}
                            <MainTableCellWrapper
                                isCustomerTab={isCustomerTab}
                                valueType={valueType}
                                data-test='main-table-cell-wrapper'
                            >
                                {sum}
                            </MainTableCellWrapper>
                        </div>

                        {methodsToRender.length > 0 ? (
                            methodsToRender.map((el) => (
                                <SingleNetDepositColumn
                                    key={el.paymentMethod.id}
                                    el={el}
                                    currency={currency}
                                    isCustomerTab={isCustomerTab}
                                />
                            ))
                        ) : (
                            <EmptyNetDepositColumn isCustomerTab />
                        )}

                        {isCustomerTab && isExpiredCardsExist(expiredCards) && (
                            <ShowMoreWrapper>
                                <Button
                                    icon={showExpiredCards ? 'arrow-left' : 'arrow-right'}
                                    onClick={onShowExpiredCards}
                                    variant='primary'
                                    data-tooltip-target='tooltip-default'
                                >
                                    {showExpiredCards ? 'Less expired cards' : 'More expired card'}
                                </Button>
                            </ShowMoreWrapper>
                        )}
                    </div>
                </NetDepositContainer>
            </div>
        );
    }
);
